import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DownloadIcon from '@mui/icons-material/Download';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrainIcon from '@mui/icons-material/Train';
import UploadIcon from '@mui/icons-material/Publish';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { useSearchParams } from 'react-router-dom';
import { LoadingButton } from '@/components/shared/LoadingButton';
import type { SortFilterValue } from '@/components/shared/SortFilterDialog';
import { SortFilterControl } from '@/components/shared/SortFilterControl';
import { TableNotification } from '@/components/shared/TableNotification';
import { useUser } from '@/context/UserContext';
import { logger } from '@/services/logging';
import { UPLOAD_SUCCESS_STATUS, type ScheduleUploadResult } from '@/services/scheduling/schedulingService';
import {
  buildSupplyPointListRequest,
  downloadSupplyPoint,
  fetchSupplyPoint,
  getImportFormats,
  PAGE_TITLES,
  parsePageType,
  readDownloadError,
  SUPPLY_POINT_FILTER_FIELDS,
  SUPPLY_POINT_PAGE_SIZE_OPTIONS,
  SUPPLY_POINT_SORT_FIELDS,
  SUPPLY_POINT_TABS,
  type SupplyPointItem,
  type SupplyPointPageType,
  type SupplyPointTab,
} from '@/services/supplyPoint/supplyPointService';
import { getOrderStatusColor } from '@/utils/orderStatus';
import { ScheduleImportDialog, UploadResultDialog } from '@/pages/scheduling/ScheduleDialogs';
import { SupplyPointExportDialog } from './SupplyPointExportDialog';

interface ColumnDefinition {
  id: string;
  label: string;
  tooltip?: string;
  filterKey?: string;
  render: (item: SupplyPointItem) => React.ReactNode;
}

const HEADER_BG = '#0c479d';
const ROW_HOVER_BG = '#E3F2FC';
const ACTIVE_HEADER_SX = { color: `${HEADER_BG} !important`, fontWeight: '700 !important' };

const OUTLINED_BUTTON_SX = {
  maxHeight: '36px',
  fontSize: '13px',
  letterSpacing: '0.08em',
  color: '#0c479d',
  fontWeight: 700,
  borderColor: 'rgba(0, 0, 0, 0.12)',
};

const HEADER_BUTTON_SX = {
  maxHeight: '36px',
  fontSize: '13px',
  letterSpacing: '0.08em',
  color: '#000',
  backgroundColor: '#fff',
  borderColor: 'rgba(0, 0, 0, .12)',
  fontWeight: 700,
  '&:hover': { borderColor: 'rgba(0, 0, 0, .12)', backgroundColor: '#fff' },
};

const TABLE_SX = {
  fontFamily: 'Roboto, sans-serif',
  backgroundColor: 'white',
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontSize: '14px',
    fontWeight: 500,
    padding: '8px 6px',
    verticalAlign: 'middle',
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: 'white',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: '14px',
    padding: '7px 6px',
    color: 'rgba(0, 0, 0, 0.87)',
    whiteSpace: 'nowrap',
  },
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : value;
};

const formatNumber = (value?: number | null) => (
  value == null ? '' : Number(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 3 })
);

const stripLeadingZeros = (value?: string | null) => (value ?? '').replace(/^0+(?=.)/, '');

const getRowKey = (item: SupplyPointItem) => `${item.orderNumber}|${item.itemNumber ?? ''}|${item.deliveryNumber ?? ''}`;

const Truncated: React.FC<{ value?: string | null; max?: number }> = ({ value, max = 12 }) => {
  const text = value ?? '';
  return text.length > max
    ? <Tooltip title={text}><span>{`${text.slice(0, max)}...`}</span></Tooltip>
    : <span>{text}</span>;
};

const SHIPPING_ICONS: Record<SupplyPointTab, React.ElementType> = {
  TRUCK: LocalShippingIcon,
  RAIL: TrainIcon,
  MCT: LocalShippingIcon,
  'MARINE BULK': DirectionsBoatIcon,
};

const getTransportColumn = (tab: SupplyPointTab): ColumnDefinition => {
  switch (tab) {
    case 'TRUCK':
      return { id: 'truckNumber', label: 'Truck Number', filterKey: 'TruckNumber', render: (item) => item.truckNumber };
    case 'RAIL':
      return { id: 'railCar', label: 'Railcar ID', filterKey: 'RailCar', render: (item) => item.railCar };
    default:
      return { id: 'vesselNumber', label: 'Vessel Number', filterKey: 'VesselNumber', render: (item) => item.vesselNumber };
  }
};

const getColumns = (tab: SupplyPointTab, pageType: SupplyPointPageType): ColumnDefinition[] => {
  const ShippingIcon = SHIPPING_ICONS[tab];
  const isLoading = pageType === 'loading';

  const identity: ColumnDefinition[] = [
    { id: 'orderNumber', label: 'Order #', filterKey: 'OrderNumber', render: (item) => item.orderNumber },
    { id: 'itemNumber', label: 'Item', filterKey: 'ItemNumber', render: (item) => item.itemNumber },
    { id: 'orderType', label: 'Type', filterKey: 'OrderType', render: (item) => <Truncated value={item.orderType} /> },
    {
      id: 'shipToDescription',
      label: 'Ship-To',
      filterKey: 'ShipToDescription',
      render: (item) => <Truncated value={item.shipToDescription} max={20} />,
    },
    {
      id: 'shipToPlantCode',
      label: 'Ship-To Code',
      filterKey: 'ShipToPlantCode',
      render: (item) => (
        <Tooltip title={item.shipToPlantCode ?? ''}><span>{stripLeadingZeros(item.shipToPlantCode)}</span></Tooltip>
      ),
    },
    {
      id: 'shippingCondition',
      label: 'Ship Cond.',
      filterKey: 'ShippingCondition',
      render: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ShippingIcon sx={{ fontSize: 20 }} />
          <span>{item.shippingCondition}</span>
        </Box>
      ),
    },
    ...(tab === 'RAIL' ? [] : [{ id: 'routeCode', label: 'Route', filterKey: 'RouteCode', render: (item: SupplyPointItem) => item.routeCode }]),
    {
      id: 'materialCode',
      label: 'Material',
      filterKey: 'MaterialCode',
      render: (item) => <Truncated value={stripLeadingZeros(item.materialCode)} />,
    },
    { id: 'package', label: 'Package', filterKey: 'Package', render: (item) => <Truncated value={item.package} /> },
  ];

  const loadingOnly: ColumnDefinition[] = [
    { id: 'quantity', label: 'Qty', filterKey: 'Quantity', render: (item) => formatNumber(item.quantity) },
    { id: 'unitOfMeasure', label: 'UoM', filterKey: 'UnitOfMeasure', render: (item) => item.unitOfMeasure },
    { id: 'grossWeight', label: 'Gross Weight', filterKey: 'GrossWeight', render: (item) => formatNumber(item.grossWeight) },
  ];

  const carrier: ColumnDefinition[] = [
    { id: 'carrier', label: 'Carrier', filterKey: 'Carrier', render: (item) => <Truncated value={item.carrier} /> },
    getTransportColumn(tab),
  ];

  const plannedDates: ColumnDefinition[] = [
    {
      id: 'plannedLoadDate',
      label: 'P. Load Date',
      tooltip: 'Planned Load Date',
      filterKey: 'PlannedLoadDate',
      render: (item) => formatDate(item.plannedLoadDate),
    },
    {
      id: 'requestedDeliveryDate',
      label: 'Req. Delv. Date',
      tooltip: 'Requested Delivery Date',
      filterKey: 'RequestedDeliveryDate',
      render: (item) => formatDate(item.requestedDeliveryDate),
    },
  ];

  const loaded: ColumnDefinition[] = [
    { id: 'loadDate', label: 'Load Date', filterKey: 'LoadDate', render: (item) => formatDate(item.loadDate) },
    { id: 'loadedQuantity', label: 'Loaded Qty', render: (item) => formatNumber(item.loadedQuantity) },
    { id: 'loadedUOM', label: 'Loaded UoM', render: (item) => item.loadedUOM },
  ];

  const delivered: ColumnDefinition[] = [
    {
      id: 'requestedDeliveryDate',
      label: 'Req. Delv. Date',
      tooltip: 'Requested Delivery Date',
      filterKey: 'RequestedDeliveryDate',
      render: (item) => formatDate(item.requestedDeliveryDate),
    },
    { id: 'deliveryDate', label: 'Delivered Date', filterKey: 'DeliveryDate', render: (item) => formatDate(item.deliveryDate) },
    { id: 'deliveryQuantity', label: 'Delivered Qty', render: (item) => formatNumber(item.deliveryQuantity) },
    {
      id: 'deliveryUnitOfMeasure',
      label: 'Delivered UoM',
      filterKey: 'DeliveryUnitOfMeasure',
      render: (item) => item.deliveryUnitOfMeasure,
    },
  ];

  return isLoading
    ? [...identity, ...loadingOnly, ...carrier, ...plannedDates, ...loaded]
    : [...identity, ...carrier, ...loaded, ...delivered];
};

export const SupplyPointPage: React.FC = () => {
  const { getBusinessUnit, userData } = useUser();
  const businessUnit = getBusinessUnit();
  const [searchParams] = useSearchParams();
  const pageType = parsePageType(searchParams.get('type'));

  const [tab, setTab] = useState<SupplyPointTab>('TRUCK');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(SUPPLY_POINT_PAGE_SIZE_OPTIONS[0]);
  const [sorts, setSorts] = useState<SortFilterValue[]>([]);
  const [filters, setFilters] = useState<SortFilterValue[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState<ScheduleUploadResult | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setPage(0);
    setSorts([]);
    setFilters([]);
    setErrorMessage(null);
  }, [pageType]);

  const { params, ignoredFilters } = useMemo(
    () => buildSupplyPointListRequest({ tab, pageType, page: page + 1, pageSize, sorts, filters }),
    [tab, pageType, page, pageSize, sorts, filters],
  );

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['supplyPoint', params],
    queryFn: () => fetchSupplyPoint(params),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const columns = useMemo(() => getColumns(tab, pageType), [tab, pageType]);
  const importFormats = useMemo(() => getImportFormats(tab), [tab]);
  const importNoun = pageType === 'loading' ? 'Loadings' : 'Deliveries';

  const activeFilterColumns = useMemo(
    () => new Set(filters.map((filter) => filter.property.replace(/(Min|Max)$/, ''))),
    [filters],
  );

  const resetToFirstPage = () => setPage(0);

  const handleTabChange = (_: React.SyntheticEvent, nextTab: SupplyPointTab) => {
    setTab(nextTab);
    resetToFirstPage();
  };

  const handleExport = async (format: string) => {
    setExportOpen(false);
    setIsExtracting(true);
    setErrorMessage(null);

    try {
      const { blob, filename } = await downloadSupplyPoint({
        tab,
        pageType,
        format,
        source: businessUnit,
        userId: userData.userId,
        filters,
      });
      saveAs(blob, filename);
      // Rail exports advance IM-Confirmed deliveries, so the table is stale afterwards.
      void refetch();
    } catch (exportError) {
      logger.error('Supply point export failed', exportError as Error);
      setErrorMessage(await readDownloadError(exportError));
    } finally {
      setIsExtracting(false);
    }
  };

  const handleImportComplete = (result: ScheduleUploadResult) => {
    setImportOpen(false);
    setUploadResult(result);
  };

  const handleUploadResultClose = () => {
    if (uploadResult?.status === UPLOAD_SUCCESS_STATUS) void refetch();
    setUploadResult(null);
  };

  const notificationType = isError
    ? ((error as { code?: string })?.code === 'ECONNABORTED' ? 'Timeout' : 'Error')
    : isFetching
      ? 'Loading'
      : total === 0
        ? (filters.length > 0 ? 'Filter' : 'Empty')
        : null;
  const isEmptyResult = notificationType === 'Filter' || notificationType === 'Empty';
  const showTable = !notificationType || isEmptyResult;

  return (
    <Box sx={{ p: 0, m: 0, minHeight: 'calc(100vh - 64px)', backgroundColor: '#f2f4f6' }}>
      <Toolbar
        disableGutters
        sx={{
          minHeight: '64px',
          width: '100%',
          px: '16px',
          backgroundColor: `${HEADER_BG} !important`,
          color: '#fff !important',
          alignItems: 'center',
        }}
      >
        <Typography id="qa-supplyPoint-title" sx={{ color: '#fff', fontSize: '28px', fontWeight: 400, lineHeight: '36px' }}>
          {PAGE_TITLES[pageType]}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton
          id="qa-supplyPoint-import-btn"
          variant="outlined"
          startIcon={<UploadIcon />}
          loading={importOpen}
          loadingText="IMPORTING"
          onClick={() => setImportOpen(true)}
          sx={HEADER_BUTTON_SX}
        >
          {`IMPORT ${importNoun.toUpperCase()}`}
        </LoadingButton>
      </Toolbar>

      <Tabs value={tab} onChange={handleTabChange} sx={{ backgroundColor: '#fff', px: 2 }}>
        {SUPPLY_POINT_TABS.map((item) => (
          <Tab key={item} id={`qa-${item.toLowerCase().replace(/\s+/g, '-')}-lnk`} value={item} label={item} />
        ))}
      </Tabs>

      <Box sx={{ m: '20px' }}>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mx: 2, mt: 2 }}>{errorMessage}</Alert>
        )}
        {ignoredFilters.length > 0 && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            {`Not applied: ${ignoredFilters.map((filter) => filter.label).join(', ')}. `
              + 'Only one Min/Max bound per date column is supported, and dates must be valid.'}
          </Alert>
        )}

        <Toolbar
          disableGutters
          sx={{ px: 0, py: 0, mb: '20px', minHeight: '48px !important', gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between' }}
        >
          <Button
            id="qa-refreshTable-btn"
            variant="outlined"
            onClick={() => void refetch()}
            disabled={isFetching}
            sx={OUTLINED_BUTTON_SX}
          >
            REFRESH TABLE
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <SortFilterControl
              mode="sort"
              fields={SUPPLY_POINT_SORT_FIELDS}
              activeFields={sorts}
              onApply={(next) => { setSorts(next); resetToFirstPage(); }}
              buttonId="qa-table-sort-btn"
              buttonSx={OUTLINED_BUTTON_SX}
            />
            <SortFilterControl
              mode="filter"
              fields={SUPPLY_POINT_FILTER_FIELDS}
              activeFields={filters}
              onApply={(next) => { setFilters(next); resetToFirstPage(); }}
              buttonId="qa-table-filter-btn"
              buttonSx={OUTLINED_BUTTON_SX}
              open={filterDialogOpen}
              onOpenChange={setFilterDialogOpen}
            />
            <LoadingButton
              id="qa-exportSupplyPoint-btn"
              variant="contained"
              disableElevation
              startIcon={<DownloadIcon />}
              loading={isExtracting}
              loadingText="EXPORTING"
              onClick={() => setExportOpen(true)}
              sx={{
                maxHeight: '36px',
                fontSize: '13px',
                letterSpacing: '0.08em',
                color: 'common.white',
                backgroundColor: `${HEADER_BG} !important`,
              }}
            >
              EXPORT
            </LoadingButton>
          </Box>
        </Toolbar>

        <Paper square elevation={0} sx={{ width: '100%', overflow: 'hidden', backgroundColor: '#fff' }}>
          {notificationType && !isEmptyResult && (
            <TableNotification type={notificationType} onTryAgainEvent={() => void refetch()} />
          )}

          {showTable && (
            <>
              <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          id={`qa-supplyPoint-${column.id}-hdr`}
                          sx={activeFilterColumns.has(column.filterKey ?? '') ? ACTIVE_HEADER_SX : undefined}
                        >
                          {column.tooltip ? <Tooltip title={column.tooltip}><span>{column.label}</span></Tooltip> : column.label}
                        </TableCell>
                      ))}
                      <TableCell
                        id="qa-supplyPoint-shipmentStatus-hdr"
                        sx={activeFilterColumns.has('ShipmentStatus') ? ACTIVE_HEADER_SX : undefined}
                      >
                        Shipment Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${getRowKey(item)}-${index}`} hover sx={{ '&:hover': { backgroundColor: ROW_HOVER_BG } }}>
                        {columns.map((column) => (
                          <TableCell key={column.id}>{column.render(item)}</TableCell>
                        ))}
                        <TableCell id="qa-supplyPoint-shipmentStatus" sx={{ minWidth: 190 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiberManualRecordIcon sx={{ fontSize: 12, color: getOrderStatusColor(item.shipmentStatus) }} />
                            <span>{item.shipmentStatus || '*UNMAPPED*'}</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {isEmptyResult ? (
                <TableNotification
                  type={notificationType}
                  onEditFilterEvent={() => setFilterDialogOpen(true)}
                />
              ) : (
              <TablePagination
                id="qa-paginator"
                component="div"
                count={total}
                page={page}
                labelRowsPerPage="Items per page:"
                rowsPerPage={pageSize}
                rowsPerPageOptions={SUPPLY_POINT_PAGE_SIZE_OPTIONS}
                showFirstButton
                showLastButton
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); resetToFirstPage(); }}
              />
              )}
            </>
          )}
        </Paper>
      </Box>

      <SupplyPointExportDialog
        open={exportOpen}
        tab={tab}
        onClose={() => setExportOpen(false)}
        onDownload={(format) => void handleExport(format)}
      />
      <ScheduleImportDialog
        open={importOpen}
        tab={tab}
        source={businessUnit}
        formats={importFormats}
        title={`Import ${importNoun}`}
        submitLabel="IMPORT"
        onClose={() => setImportOpen(false)}
        onComplete={handleImportComplete}
      />
      <UploadResultDialog
        result={uploadResult}
        operation={`Bulk ${importNoun.toLowerCase()}`}
        type={importNoun.toLowerCase()}
        onClose={handleUploadResultClose}
      />
    </Box>
  );
};
